<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.product_id }}</td>-->
          <td align="center">
            <a
              v-if="props.item.product_image"
              class="ma-2 image-frame"
              @click=";(imageSrc = props.item.product_image), (dialogs.imageViewerDialog = true)"
              :style="
                `
                display:block;
                background: #EEE url(${baseUrls.images}/${props.item.product_image}) no-repeat;
                background-position: center center;
                background-size: cover;
                width: 80px;
                height: 60px;
              `
              "
            />
            <div
              v-else
              class="ma-2 image-frame"
              :style="
                `
                display:block;
                background: rgba(3, 13, 39, 0.9);
                width: 80px;
                height: 60px;
              `
              "
            />
          </td>

          <td>{{ props.item.product_name }}</td>
          <td>{{ props.item.product_barcode }}</td>
          <td>{{ props.item.quantity | currency }}</td>
          <td>{{ props.item.quantity_alarm }}</td>
          <td>{{ props.item.shortage }}</td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />

      <app-image-viewer
        :imageSrc="imageSrc"
        :maxWidth="700"
        :imageViewerDialog="dialogs.imageViewerDialog"
        @hidemodal="dialogs.imageViewerDialog = false"
      />

      <print-export-component
        :printExportDialog="dialogs.printExportDialog"
        :tableHeader="tableHeader"
        :tableBody="tableBody"
        :clickedButton="clickedButton"
        @hidemodal="dialogs.printExportDialog = false"
      />
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    AppImageViewer: () => import('@/views/_partials/AppImageViewer')
  },

  data() {
    return {
      dialogs: {
        imageViewerDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'product_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'product_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Product Image'), value: '', align: 'center' },
        { text: this.$t('Product Name'), value: 'product_name' },
        { text: this.$t('Barcode'), value: 'product_barcode' },
        { text: this.$t('Quantity'), value: 'quantity' },
        { text: this.$t('Quantity Alarm'), value: 'quantity_alarm' },
        { text: this.$t('Shortage'), value: 'shortage' }
      ],
      allowGettingData: true,
      imageSrc: null
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'inventoryShortages/getTotalInventoryShortageList',
      baseUrls: 'misc/getBaseUrls'
    })
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Product Shortages'),
      icon: 'fa-dolly'
    })
  },

  watch: {
    pagination: {
      immediate: false,
      deep: false,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'inventoryShortages/updateTotalInventoryShortageList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('product_shortage_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    }
  }
}
</script>

<style scoped />
